import { useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api';
import { ErrorBox, money } from '../components/DataTable';
import { CalcNote } from '../components/CalcNote';

/**
 * 경영 분석 › 수리비 — 시트 `직영차량수리비_YYYY` 「통계-A」 대체(10-27). 서버 `listProfitRepair`(`functions/src/profitDirect.ts`).
 * 재무추가 계정 「수리비」 공제 · 차주 「동영」 · 차량 × 정산월. 굳힌 달은 기록, 그 밖의 달은 지금 원장.
 */

const errMsg = (e: unknown) => (e instanceof Error ? e.message : String(e));
const thisYear = Number(new Date(Date.now() + 9 * 3600_000).toISOString().slice(0, 4));
const MONTHS = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0'));

export default function ProfitRepair() {
  const [error, setError] = useState<string | null>(null);
  const [yr, setYr] = useState(thisYear);
  const [q, setQ] = useState('');
  const [data, setData] = useState<{ rows: { mon: string; car_no: string; amt: string }[]; months: { mon: string; source: string }[] } | null>(null);

  useEffect(() => {
    let cancelled = false;
    setData(null); setError(null);
    api.listProfitRepair({ yr }).then((r) => { if (!cancelled) setData(r); }).catch((e) => { if (!cancelled) setError(errMsg(e)); });
    return () => { cancelled = true; };
  }, [yr]);

  const { cars, monTot, total } = useMemo(() => {
    const by = new Map<string, Record<string, number>>();
    const mt: Record<string, number> = {};
    for (const r of data?.rows ?? []) {
      const m = r.mon.slice(5), a = Number(r.amt);
      const c = by.get(r.car_no) ?? {}; c[m] = (c[m] ?? 0) + a; by.set(r.car_no, c);
      mt[m] = (mt[m] ?? 0) + a;
    }
    const list = [...by.entries()].map(([car, v]) => ({ car, v, sum: Object.values(v).reduce((s, x) => s + x, 0) })).sort((a, b) => b.sum - a.sum);
    return { cars: list, monTot: mt, total: Object.values(mt).reduce((s, x) => s + x, 0) };
  }, [data]);
  const shown = useMemo(() => { const w = q.trim().replace(/\s/g, ''); return w ? cars.filter((c) => c.car.includes(w)) : cars; }, [cars, q]);
  const src = Object.fromEntries((data?.months ?? []).map((m) => [m.mon.slice(5), m.source]));

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <select className="field w-auto" value={yr} onChange={(e) => setYr(Number(e.target.value))} aria-label="연도">
          {Array.from({ length: thisYear - 2023 }, (_, i) => thisYear - i).map((y) => <option key={y} value={y}>{y}년</option>)}
        </select>
        <input className="field w-48" placeholder="차량번호" value={q} onChange={(e) => setQ(e.target.value)} />
        <CalcNote>재무추가 계정 「수리비」 공제 중 차주가 「동영」인 줄 · 정산월 기준. 「*」 달은 굳히기 전이라 지금 원장으로 센 값입니다(월 수익 굳히기 때 같이 굳습니다).</CalcNote>
        <span className="ml-auto text-xs text-ink-soft">{cars.length}대 · 합계 {money(total)}</span>
      </div>
      {error && <ErrorBox message={error} />}
      {!data && !error && <div className="py-8 text-center text-sm text-ink-soft">세는 중…</div>}
      {data && (
        <div className="card max-h-[calc(100vh-13rem)] overflow-auto">
          <table className="w-full min-w-[64rem] text-sm tabular-nums">
            <thead className="sticky top-0 z-10 text-xs text-ink-soft [&_th]:bg-white">
              <tr className="border-b border-line">
                <th className="px-2 py-1.5 text-left">차량</th>
                {MONTHS.map((m) => (
                  <th key={m} className="px-2 py-1.5 text-right" title={src[m] === 'live' ? '굳히기 전 — 지금 원장' : src[m] === 'sheet' ? '시트에서 옮긴 기록' : src[m] === 'web' ? '굳힌 기록' : ''}>
                    {Number(m)}월{src[m] === 'live' ? '*' : ''}
                  </th>
                ))}
                <th className="px-2 py-1.5 text-right">합계</th>
              </tr>
              <tr className="border-b border-line font-medium text-ink">
                <th className="px-2 py-1 text-left">월별</th>
                {MONTHS.map((m) => <th key={m} className="px-2 py-1 text-right">{monTot[m] ? money(monTot[m]) : ''}</th>)}
                <th className="px-2 py-1 text-right">{money(total)}</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((c) => (
                <tr key={c.car} className="border-b border-line/60 hover:bg-surface-soft">
                  <td className="whitespace-nowrap px-2 py-1">{c.car}</td>
                  {MONTHS.map((m) => <td key={m} className="px-2 py-1 text-right">{c.v[m] ? money(c.v[m]) : ''}</td>)}
                  <td className="px-2 py-1 text-right font-medium">{money(c.sum)}</td>
                </tr>
              ))}
              {shown.length === 0 && <tr><td colSpan={14} className="py-6 text-center text-ink-soft">수리비가 없습니다.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
